const PGAdapter = require('./../common/pg-adapter')
const keyBy = require('lodash/keyBy')
const get = require('lodash/get')

class PricePGAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'id',
      'sku',
      'date',
      'buyprice',
      'sellprice',
      'discount',
      'markup',
      'service_id'
    ]
    const virtualColumns = [
      'product',
      'market',
      'month'
    ]
    const opts = {
      addUuid: true,
      strictColumns: false,
      virtualColumns
    }
    super(pgConnection, 'real.price_history', username, columns, 'id', {}, logger, opts)
  }
  async getList ({ ordering = this.idColumn, filter = {}, limit = 50, offset = 0 } = {}) {
    const tableExpression = `
      (
        select
          ph.id,
          p.product_id as sku,
          p.product,
          p.market,
          p.supplier,
          ph.date,
          ph.month,
          ph.buyprice,
          ph.sellprice,
          ph.discount
          from
          real.price_history ph inner join avocado.vw_products p on p.product_id = ph.sku
      ) t
    `
    return PGAdapter.prototype.getList.call(this, {ordering, filter, limit, offset, tableExpression})
  }

  async create (data) {
    const res = await PGAdapter.prototype.create.call(this, data)
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async upsert (data) {
    const res = await PGAdapter.prototype.upsert.call(this, data, {conflictIdColumns: ['sku', 'date']})
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async update (data) {
    const res = await PGAdapter.prototype.update.call(this, data)
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async delete (id) {
    const obj = await this.getOne(id)
    if (obj) {
      const res = await PGAdapter.prototype.delete.call(this, id)
      if (res) {
        await this.clearCaches(obj)
      }
      return res
    }
  }

  async clearCaches ({date, sku}) {
    if (date && sku) {
      this.logger.debug('clearing cached values')
      await this.pgConnection.query(`DELETE FROM real.buyprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`DELETE FROM real.sellprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`
        UPDATE real.presentation_cost_basis_base
        SET _deleted=true
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
      await this.pgConnection.query(`
        DELETE
        FROM real.presentation_cost_basis
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
    }
  }

  /**
   * @param {Object} params
   * @param {string[]} params.skus
   * @param {string} params.locationFsid
   * @param {string} params.serviceId
   * @param {string|Date} params.referenceDate
   * @returns {Promise<{referenceDate: string, prices: any[]}>}
   */
  async getPriceList ({ skus, locationFsid, serviceId, referenceDate }, pgClient) {
    const normalizedSkus = Array.isArray(skus) && skus.length > 0 ? skus.map(sku => sku.replace('product:', '')) : null
    const normalizedReferenceDate = referenceDate || null
    const queryParams = [normalizedSkus, normalizedReferenceDate, locationFsid || null, serviceId || null]

    const query = `
      with product_base as (
        select
          p.old_sku_id as sku,
          m.product_market_code,
          m.aliases,
          m.id as market_id,
          p.vat
        from avocado.data_productsku p
        left join avocado.data_market m on p.market_id = m.id
        where ($1::text[] is null or p.old_sku_id = any($1::text[]))
      ),
      prices as (
        select
          sell.sell_price_id as "sellpriceId",
          buy.buy_price_id as "buypriceId",
          concat('product:', pb.sku) as sku,
          pb.vat,
          avocado.round_half_even(sell.sellprice, 2)::float as sellprice,
          avocado.round_half_even(buy.buyprice, 2)::float as buyprice,
          avocado.round_half_even(sell.discount, 2) as discount,
          avocado.round_half_even(markup.markup * markup.financing_multiplier * (markup.sellprice / (1 + markup.markup + markup.financing_multiplier * markup.markup + markup.delivery_multiplier * markup.markup)), 2) as "financingMarkup",
          avocado.round_half_even(markup.markup * markup.delivery_multiplier * (markup.sellprice / (1 + markup.markup + markup.financing_multiplier * markup.markup + markup.delivery_multiplier * markup.markup)), 2) as "deliveryMarkup"
        from product_base pb
        left join lateral (
          select
            ph.id as sell_price_id,
            ph.sellprice,
            ph.discount,
            ph.date
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          where ph.sku = pb.sku
            and ph.sellprice is not null
            and ph.date <= coalesce($2, now())
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) sell on true
        left join lateral (
          select
            ph.id as buy_price_id,
            ph.buyprice,
            ph.date
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          where ph.sku = pb.sku
            and ph.buyprice is not null
            and ph.date <= coalesce($2, now())
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) buy on true
        left join lateral (
          select
            ph.id as markup_id,
            ph.sku,
            ph.sellprice,
            ph.markup,
            ph.date,
            mp.financing_multiplier,
            mp.delivery_multiplier
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          left join avocado.data_markupmultiplierhistory mp
            on ph.date::date = mp.date::date
           and (mp.market_id = any(pb.aliases) or mp.market_id = pb.market_id)
          where ph.sku = pb.sku
            and coalesce(ph.markup, 0) > 0
            and ph.date <= coalesce($2, now())
            and mp.id is not null
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) markup on true
      )
      select
        coalesce((select jsonb_agg(p) from (select * from prices) p), '[]'::jsonb) as prices,
        coalesce($2, now()) as "referenceDate"
    `
    const client = pgClient || this.pgConnection
    const { rows } = await client.query(query, queryParams)
    const response = rows[0]
    response.referenceDate = new Date(response.referenceDate).toISOString()
    return response
  }

  async getCurrentPriceList (serviceId, { productGroup = 'all' }) {
    let productGroupFilter = ''
    if (productGroup === 'only-core') {
      productGroupFilter = ' and ps.phase = 1'
    }
    if (productGroup === 'only-ppmv') {
      productGroupFilter = ' and coalesce(ps.ppmv, false) = true '
    }
    if (productGroup === 'only-ppmv-core') {
      productGroupFilter = ' and coalesce(ps.ppmv_core, false) = true '
    }

    const {rows} = await this.pgConnection.query(`
      select
        concat('product:',ps.old_sku_id) as id,
        ps.display_name as name,
        ps.vat as "hasVat",
        pb.category,
        pb.manufacturer,
        ps.available
      from avocado.data_market m
      left join avocado.data_productsku ps on m.id = ps.market_id
      left join avocado.data_productpresentation pp on ps.presentation_id = pp.id
      left join avocado.data_productbase pb on pb.id = pp.product_id
      where fs_service_id = $1
        and lower(ps.visibility) != 'discontinued'
        and coalesce(ps._deleted, false) != true
        ${productGroupFilter}
    `, [serviceId])

    if (rows.length > 0) {
      const { prices, referenceDate } = await this.getPriceList({ skus: rows.map(r => r.id) })
      const pricesByProductId = keyBy(prices, 'sku')
      const categories = new Set()
      const manufacturers = new Set()
      const productRows = rows.map((row) => {
        if (row.category != null) {
          categories.add(row.category)
        }
        if (row.manufacturer != null) {
          manufacturers.add(row.manufacturer)
        }
        return {
          ...row,
          price: get(pricesByProductId, `${row.id}.sellprice`, null)
        }
      })

      return {
        date: referenceDate,
        productRows,
        totalCategories: categories.size,
        totalManufacturers: manufacturers.size,
        totalSKUs: rows.length
      }
    }

    return {
      date: new Date().toJSON(),
      productRows: [],
      totalCategories: 0,
      totalManufacturers: 0,
      totalSKUs: 0
    }
  }

  async getUpcomingPrices ({ skus, locationFsid, serviceId }) {
    const normalizedSkus = Array.isArray(skus) && skus.length > 0 ? skus.map(sku => sku.replace('product:', '')) : null
    const queryParams = [normalizedSkus, locationFsid || null, serviceId || null]
    const {rows} = await this.pgConnection.query(`
      with product_base as (
        select unnest($1::text[]) as sku
      ),
      upcoming_prices as (
        select
          sell.sell_price_id as "sellpriceId",
          concat('product:', pb.sku) as sku,
          avocado.round_half_even(sell.sellprice, 2)::float as sellprice,
          sell."date"
        from product_base pb
        left join lateral (
          select
            ph.id as sell_price_id,
            ph.sellprice,
            ph.discount,
            ph.date
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          where ph.sku = pb.sku
            and ph.sellprice is not null
            and ph.date > now()
            and (
              ($2::text is null and $3::text is null and ph.service_id is null and ph.location_id is null) or
              ($2::text is not null and $3::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $2) or
              ($2::text is null and $3::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $3) or
              ($2::text is not null and $3::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $2 and s.fsid = $3)
            )
          order by ph.date asc
          limit 1
        ) sell on true
      )
      select 
        coalesce((select jsonb_agg(p) from (select * from upcoming_prices where "sellpriceId" is not null) p), '[]'::jsonb) as "upcomingPrices",
        coalesce((select min(date) from upcoming_prices), now()) as "effectiveDate"
    `, queryParams)
    return rows[0]
  }

  async getUpcomingPriceList (serviceId, { productGroup = 'all' }) {
    let productGroupFilter = ''
    if (productGroup === 'only-core') {
      productGroupFilter = ' and ps.phase = 1'
    }
    if (productGroup === 'only-ppmv') {
      productGroupFilter = ' and coalesce(ps.ppmv, false) = true '
    }
    if (productGroup === 'only-ppmv-core') {
      productGroupFilter = ' and coalesce(ps.ppmv_core, false) = true '
    }

    const {rows} = await this.pgConnection.query(`
      select
        concat('product:',ps.old_sku_id) as id,
        ps.display_name as name,
        ps.vat as "hasVat",
        pb.category,
        pb.manufacturer,
        ps.available
      from avocado.data_market m
      left join avocado.data_productsku ps on m.id = ps.market_id
      left join avocado.data_productpresentation pp on ps.presentation_id = pp.id
      left join avocado.data_productbase pb on pb.id = pp.product_id
      where fs_service_id = $1
        and lower(ps.visibility) != 'discontinued'
        and coalesce(ps._deleted, false) != true
        ${productGroupFilter}
    `, [serviceId])

    if (rows.length > 0) {
      const productIds = rows.map(r => r.id)
      const [ { prices: currentPrices }, { upcomingPrices, effectiveDate } ] = await Promise.all([
        this.getPriceList({ skus: productIds }),
        this.getUpcomingPrices({ skus: productIds })
      ])

      const currentPricesByProductId = keyBy(currentPrices, 'sku')
      const upcomingPricesByProductId = keyBy(upcomingPrices, 'sku')
      const {
        totalIncreased,
        totalDecreased,
        totalUnchanged,
        categories,
        manufacturers,
        productRows
      } = rows.reduce((acc, row) => {
        const productId = row.id
        const currentPrice = get(currentPricesByProductId, `${productId}.sellprice`, 0)
        const upcomingPrice = get(upcomingPricesByProductId, `${productId}.sellprice`, 0)
        const changeValue = upcomingPrice ? upcomingPrice - currentPrice : null

        acc.manufacturers.add(row.manufacturer)
        acc.categories.add(row.category)
        if (upcomingPrice > currentPrice) {
          acc.totalIncreased += 1
        } else if (upcomingPrice && currentPrice && upcomingPrice < currentPrice) {
          acc.totalDecreased += 1
        } else {
          acc.totalUnchanged += 1
        }
        acc.productRows.push({
          ...row,
          currentPrice: {
            date: get(currentPricesByProductId, `${productId}.date`, null),
            price: currentPrice || null
          },
          price: {
            date: get(upcomingPricesByProductId, `${productId}.date`, null),
            price: upcomingPrice || null
          },
          changeValue: changeValue,
          change: changeValue ? (changeValue / currentPrice) * 100 : null
        })

        return acc
      }, {
        totalIncreased: 0,
        totalDecreased: 0,
        totalUnchanged: 0,
        categories: new Set(),
        manufacturers: new Set(),
        productRows: []
      })

      return {
        productRows,
        totalCategories: categories.size,
        totalManufacturers: manufacturers.size,
        totalSKUs: rows.length,
        effectiveDate,
        totalDecreased,
        totalIncreased,
        totalUnchanged
      }
    }

    return {
      productRows: [],
      totalCategories: 0,
      totalManufacturers: 0,
      totalSKUs: 0,
      effectiveDate: new Date().toJSON(),
      totalDecreased: 0,
      totalIncreased: 0,
      totalUnchanged: 0
    }
  }
}

module.exports = {PricePGAdapter}
